import { getApiUrl } from "../services/apiConfig.js";
import { todayISO, iniSemana } from "../shared/utils.js";
import { iniMes } from "../shared/utils.js";
import { useState, useRef, useEffect } from "react";
/* Included inside the existing React bundle. Reads only aggregated new task credits. */
function addRankingPoints(value, index) {
  return (
    (Math.round(Number(value) * 1000) + Math.round(Number(index) * 1000)) / 1000
  );
}
function roundMetric(value, index) {
  const scale = 10 ** index;
  return Math.round(Number(value) * scale) / scale;
}
const taskBonusCache = new Map();
function getTaskBonusIdentity() {
  try {
    const session = JSON.parse(
      sessionStorage.getItem("auth_session") || "null",
    );
    if (!session || !["admin", "aluno"].includes(session.tipo)) return null;
    const data = session.data || {};
    const headers = {};
    if (session.tipo === "admin") {
      if (data.token) headers["X-Admin-Token"] = data.token;
      if (data.learning_admin_token)
        headers["X-Learning-Token"] = data.learning_admin_token;
    } else {
      if (!data.learning_token || !Number(data.id)) return null;
      headers["X-Learning-Token"] = data.learning_token;
      headers["X-Student-Id"] = String(data.id);
    }
    // Identity stays in memory; no token is stored in a report, cache on disk or URL.
    return {
      key: JSON.stringify([session.tipo, data.id, headers]),
      headers,
    };
  } catch {
    return null;
  }
}
function getTaskBonusPeriod(period) {
  const day = todayISO();
  return period === "hoje"
    ? {
        from: day,
        to: day,
      }
    : period === "semana"
      ? {
          from: iniSemana(),
        }
      : period === "mes"
        ? {
            from: iniMes(),
          }
        : period === "ano"
          ? {
              from: new Date(Date.now() - 31536e6).toISOString().slice(0, 10),
            }
          : {};
}
function useTaskBonusTotals(
  enabled,
  studentId = null,
  from = null,
  index = null,
  notify = null,
  includeActivity = false,
) {
  // Manter a mesma identidade entre renderizações, sem guardar tokens fora da memória.
  const identityRef = useRef(null);
  const currentIdentity = getTaskBonusIdentity();
  if (identityRef.current?.key !== currentIdentity?.key) identityRef.current = currentIdentity;
  const identity = identityRef.current;
  const key = JSON.stringify([
    identity?.key || "",
    studentId || null,
    from || null,
    index || null,
    includeActivity,
  ]);
  const [revision, setRevision] = useState(0);
  const [snapshot, setSnapshot] = useState({
    key: "",
    points: {},
    activityTotals: {},
    loading: false,
    error: "",
  });
  const message = useRef(notify);
  message.current = notify;
  useEffect(() => {
    if (!enabled || !identity) return;
    const refresh = () => {
      taskBonusCache.delete(key);
      setRevision((value) => value + 1);
    };
    const focus = () => {
      if (!document.hidden) refresh();
    };
    window.addEventListener("dm-task-bonus-changed", refresh);
    window.addEventListener("focus", focus);
    if (includeActivity) {
      window.addEventListener("dm-learning-changed", refresh);
      document.addEventListener("visibilitychange", focus);
    }
    const timer = includeActivity ? window.setInterval(focus, 30000) : null;
    return () => {
      window.removeEventListener("dm-task-bonus-changed", refresh);
      window.removeEventListener("focus", focus);
      window.removeEventListener("dm-learning-changed", refresh);
      document.removeEventListener("visibilitychange", focus);
      if (timer) window.clearInterval(timer);
    };
  }, [enabled, identity, key, includeActivity]);
  useEffect(() => {
    if (!enabled || !identity) return;
    let alive = true;
    const abort = new AbortController();
    const cached = taskBonusCache.get(key);
    if (cached && Date.now() - cached.at < 30000) {
      setSnapshot({
        ...cached.value,
        key,
        loading: false,
      });
      return () => {
        alive = false;
        abort.abort();
      };
    }
    setSnapshot((previous) => ({
      key,
      points: previous.key === key ? previous.points : {},
      activityTotals: previous.key === key ? previous.activityTotals : {},
      loading: true,
      error: "",
    }));
    (async () => {
      const points = {};
      const activityTotals = {};
      let page = 1;
      let totalPages = 1;
      let scope = null;
      do {
        if (getTaskBonusIdentity()?.key !== identity.key)
          throw Error("A conta mudou. Entre novamente.");
        const params = {
          action: "ranking",
          page,
          limit: 500,
        };
        if (includeActivity) params.include_activity = 1;
        if (studentId) params.student_id = studentId;
        if (from) params.from = from;
        if (index) params.to = index;
        const response = await fetch(
          getApiUrl("/api/task-bonus.php?" + new URLSearchParams(params)),
          {
            headers: identity.headers,
            signal: abort.signal,
            credentials: "same-origin",
            cache: "no-store",
          },
        );
        const json = await response.json();
        if (!response.ok || json.status !== "success")
          throw Object.assign(
            Error(json.message || "Não foi possível atualizar os bônus."),
            {
              status: response.status,
            },
          );
        const data = json.data;
        if (
          !Array.isArray(data?.items) ||
          data.readonly !== true ||
          Number(data.page) !== page ||
          !Number.isInteger(Number(data.totalPages)) ||
          Number(data.totalPages) < 1
        )
          throw Error("Resposta de pontuação inválida.");
        if (scope && JSON.stringify(scope) !== JSON.stringify(data.scope))
          throw Error("O acesso mudou durante a consulta.");
        scope = data.scope;
        for (const item of data.items) {
          const number = Number(item.aluno_id);
          const numero = Number(item.task_bonus_points);
          const manual = Number(item.manual_points ?? 0);
          if (
            !Number.isInteger(number) ||
            number < 1 ||
            !Number.isFinite(numero) ||
            numero < 0 ||
            !Number.isFinite(manual)
          )
            throw Error("Pontuação registrada inválida.");
          // Replace the student's aggregated value; never sum a duplicate page or legacy log.
          points[number] = addRankingPoints(numero, manual);
          if (includeActivity) {
            const activity = item.activity;
            if (!activity || !["study_seconds", "closed", "open", "flashcards"].every(
              (field) => Number.isFinite(Number(activity[field])) && Number(activity[field]) >= 0,
            )) throw Error("Resumo de estudo indisponível.");
            activityTotals[number] = activity;
          }
        }
        totalPages = Number(data.totalPages);
        if (totalPages > 10000) throw Error("Refine a consulta de pontuação.");
        page++;
      } while (page <= totalPages);
      if (!alive || getTaskBonusIdentity()?.key !== identity.key) return;
      const value = {
        points,
        activityTotals,
        scope,
        loading: false,
        error: "",
      };
      taskBonusCache.set(key, {
        at: Date.now(),
        value,
      });
      while (taskBonusCache.size > 32)
        taskBonusCache.delete(taskBonusCache.keys().next().value);
      setSnapshot({
        ...value,
        key,
      });
    })().catch((error) => {
      if (!alive || error.name === "AbortError") return;
      const revoked = error.status === 401 || error.status === 403;
      if (revoked) taskBonusCache.delete(key);
      setSnapshot((previous) => ({
        key,
        points: !revoked && previous.key === key ? previous.points : {},
        activityTotals: !revoked && previous.key === key ? previous.activityTotals : {},
        loading: false,
        error: error.message,
      }));
      message.current?.(error.message, "er");
    });
    return () => {
      alive = false;
      abort.abort();
    };
  }, [enabled, key, revision, identity, includeActivity, from, index, studentId]);
  return enabled && identity && snapshot.key === key
    ? snapshot
    : {
        points: {},
        activityTotals: {},
        loading: false,
        error: "",
      };
}
window.DMTaskBonusRanking = Object.freeze({
  refresh() {
    taskBonusCache.clear();
    window.dispatchEvent(new Event("dm-task-bonus-changed"));
  },
});

/* Profile V10. This source is inserted in the existing React module by patch-profile.py. */
/* Inserted inside the existing React application; no auth data leaves memory. */

/* Decorative fallback for accounts without a photo. No initials or database writes. */

/* Display only: keep guide IDs, raw names, answer progress and saved metadata unchanged. */
export {
  addRankingPoints,
  roundMetric,
  taskBonusCache,
  getTaskBonusIdentity,
  getTaskBonusPeriod,
  useTaskBonusTotals,
};
